import { useCallback, useEffect, useState } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface OfficeSettingsFormState {
  officeName: string;
  legalName: string;
  documentNumber: string;
  website: string;
  email: string;
  phone: string;
  whatsapp: string;
  instagram: string;
  addressLine: string;
  addressNumber: string;
  addressComplement: string;
  district: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  lawyerName: string;
  lawyerDocument: string;
  lawyerOab: string;
  lawyerOabState: string;
  lawyerEmail: string;
  lawyerPhone: string;
  lawyerRole: string;
  bankName: string;
  bankAgency: string;
  bankAccount: string;
  bankAccountType: string;
  pixKey: string;
  pixKeyType: string;
  paymentHolderName: string;
  paymentHolderDocument: string;
  defaultCity: string;
  defaultState: string;
  defaultForum: string;
  documentFooter: string;
  signatureText: string;
  contractObservations: string;
  powerOfAttorneyObservations: string;
  defaultCurrency: string;
  defaultLanguage: string;
  timezone: string;
  dateFormat: string;
}

type OfficeSettingsResponse = {
  officeSettings?: Partial<OfficeSettingsFormState> | null;
};

type UseOfficeSettingsOptions = {
  autoLoad?: boolean;
};

export const createEmptyOfficeSettingsForm = (): OfficeSettingsFormState => ({
  officeName: '',
  legalName: '',
  documentNumber: '',
  website: '',
  email: '',
  phone: '',
  whatsapp: '',
  instagram: '',
  addressLine: '',
  addressNumber: '',
  addressComplement: '',
  district: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  lawyerName: '',
  lawyerDocument: '',
  lawyerOab: '',
  lawyerOabState: '',
  lawyerEmail: '',
  lawyerPhone: '',
  lawyerRole: '',
  bankName: '',
  bankAgency: '',
  bankAccount: '',
  bankAccountType: '',
  pixKey: '',
  pixKeyType: '',
  paymentHolderName: '',
  paymentHolderDocument: '',
  defaultCity: '',
  defaultState: '',
  defaultForum: '',
  documentFooter: '',
  signatureText: '',
  contractObservations: '',
  powerOfAttorneyObservations: '',
  defaultCurrency: '',
  defaultLanguage: '',
  timezone: '',
  dateFormat: '',
});

const toText = (value: unknown) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const normalizeOfficeSettings = (value: Partial<OfficeSettingsFormState> | null | undefined): OfficeSettingsFormState => {
  const empty = createEmptyOfficeSettingsForm();

  if (!value) {
    return empty;
  }

  return {
    officeName: toText(value.officeName),
    legalName: toText(value.legalName),
    documentNumber: toText(value.documentNumber),
    website: toText(value.website),
    email: toText(value.email),
    phone: toText(value.phone),
    whatsapp: toText(value.whatsapp),
    instagram: toText(value.instagram),
    addressLine: toText(value.addressLine),
    addressNumber: toText(value.addressNumber),
    addressComplement: toText(value.addressComplement),
    district: toText(value.district),
    city: toText(value.city),
    state: toText(value.state),
    zipCode: toText(value.zipCode),
    country: toText(value.country),
    lawyerName: toText(value.lawyerName),
    lawyerDocument: toText(value.lawyerDocument),
    lawyerOab: toText(value.lawyerOab),
    lawyerOabState: toText(value.lawyerOabState),
    lawyerEmail: toText(value.lawyerEmail),
    lawyerPhone: toText(value.lawyerPhone),
    lawyerRole: toText(value.lawyerRole),
    bankName: toText(value.bankName),
    bankAgency: toText(value.bankAgency),
    bankAccount: toText(value.bankAccount),
    bankAccountType: toText(value.bankAccountType),
    pixKey: toText(value.pixKey),
    pixKeyType: toText(value.pixKeyType),
    paymentHolderName: toText(value.paymentHolderName),
    paymentHolderDocument: toText(value.paymentHolderDocument),
    defaultCity: toText(value.defaultCity),
    defaultState: toText(value.defaultState),
    defaultForum: toText(value.defaultForum),
    documentFooter: toText(value.documentFooter),
    signatureText: toText(value.signatureText),
    contractObservations: toText(value.contractObservations),
    powerOfAttorneyObservations: toText(value.powerOfAttorneyObservations),
    defaultCurrency: toText(value.defaultCurrency),
    defaultLanguage: toText(value.defaultLanguage),
    timezone: toText(value.timezone),
    dateFormat: toText(value.dateFormat),
  };
};

export function useOfficeSettings(options: UseOfficeSettingsOptions = {}) {
  const { autoLoad = true } = options;
  const [officeSettings, setOfficeSettings] = useState<OfficeSettingsFormState | null>(null);
  const [isLoadingOfficeSettings, setIsLoadingOfficeSettings] = useState(true);
  const [isSavingOfficeSettings, setIsSavingOfficeSettings] = useState(false);
  const [officeSettingsError, setOfficeSettingsError] = useState<string | null>(null);
  const [officeSettingsSuccess, setOfficeSettingsSuccess] = useState<string | null>(null);

  const loadOfficeSettings = useCallback(async () => {
    setIsLoadingOfficeSettings(true);
    setOfficeSettingsError(null);
    setOfficeSettingsSuccess(null);

    try {
      const response = await fetchWithRetry('/api/office-settings');
      const data = (await response.json()) as OfficeSettingsResponse & { success?: boolean; error?: string };

      if (!response.ok || !data.success) {
        throw new Error(data.error || `GET /api/office-settings falhou com status ${response.status}`);
      }

      const next = data.officeSettings && Object.keys(data.officeSettings).length > 0 ? normalizeOfficeSettings(data.officeSettings) : null;
      setOfficeSettings(next);
    } catch (error) {
      console.error('Erro ao carregar configurações do escritório.', error);
      setOfficeSettings(null);
      setOfficeSettingsError('Não foi possível carregar as configurações do escritório.');
    } finally {
      setIsLoadingOfficeSettings(false);
    }
  }, []);

  const saveOfficeSettings = useCallback(async (input: OfficeSettingsFormState) => {
    setIsSavingOfficeSettings(true);
    setOfficeSettingsError(null);
    setOfficeSettingsSuccess(null);

    try {
      const response = await fetchWithRetry('/api/office-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const data = (await response.json()) as OfficeSettingsResponse & { success?: boolean; error?: string };

      if (!response.ok || !data.success || !data.officeSettings) {
        throw new Error(data.error || `PUT /api/office-settings falhou com status ${response.status}`);
      }

      const saved = normalizeOfficeSettings(data.officeSettings);
      setOfficeSettings(saved);
      setOfficeSettingsSuccess('Configurações do escritório salvas com sucesso.');
      return saved;
    } catch (error: any) {
      const message = error?.message || 'Não foi possível salvar as configurações do escritório.';
      setOfficeSettingsError(message);
      throw new Error(message);
    } finally {
      setIsSavingOfficeSettings(false);
    }
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      setIsLoadingOfficeSettings(false);
      return;
    }

    void loadOfficeSettings();
  }, [autoLoad, loadOfficeSettings]);

  return {
    officeSettings,
    isLoadingOfficeSettings,
    isSavingOfficeSettings,
    officeSettingsError,
    officeSettingsSuccess,
    loadOfficeSettings,
    saveOfficeSettings,
    setOfficeSettings,
  };
}
